import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngrx/store';
import {AppState} from '../../app/app.state';
import {SessionService} from './session.service';
import {App, APP_TYPE_PICTURE, APP_TYPE_PROP_MANAGER, AppId, AppIdMap, AppPatch, NewApp} from '../../shared/models/app';
import {AppsFetchedAll, AppsFetchedMultiple, AppsFetchedOne, AppsSavedTileGroups} from '../actions/apps.actions';
import {TileGroup} from '../../shared/models/tile-group';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {Template} from '../../shared/models/app-template/template';
import {AsideService} from './aside.service';
import {map, take, tap} from 'rxjs/operators';
import {API_BASE_URL} from './env.service';
import { ObjectType } from 'src/shared/models/object.model';
import { Permission } from 'src/shared/models/fga/permission';
import { PermissionsService } from './permissions.service';
import { TileId } from '../../shared/models/tile';

@Injectable({
  providedIn: 'root',
})
export class AppsService {
  private _currentAppId: AppId = null;

  private sessUserApps: AppIdMap = {};

  // private fetchUserAppsTrigger$: Object<any> = new Object<any>();
  private fetchUserAppsResults$: Subject<App[]>;
  private fetchedUserAppsOnce: boolean = false;


  constructor(
    private asideService: AsideService,
    private session: SessionService,
    private http: HttpClient,
    private store: Store<AppState>,
    private permissionsService: PermissionsService,
  ) {
    this.store.select('apps').subscribe(
      (state) => {
        this._currentAppId = state.currentAppId;

        for (let app of state.apps) {
          this.sessUserApps[app.ID] = app;
        }
      },
    );
  }


  get currentAppId(): AppId {
    return this._currentAppId;
  }


  fetchApp(appId: AppId): void {
    this.http.get<App>(`${API_BASE_URL}/apps/${appId}`).subscribe(
      (data: App): void => {
        const app = App.create(data);

        this.store.dispatch(new AppsFetchedOne(appId, app));
      },
    );
  }


  deleteApp$(appId: AppId): Observable<any> {
    return new Observable(
      (subscriber) => {
        this.http.delete(`${API_BASE_URL}/apps/${appId}`).subscribe(
          () => {
            subscriber.next(true);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  canManageApp$(appId: AppId): Observable<boolean> {
    const granteeId = this.session.userId;

    const userApps$ = this.getUserApps$().pipe(take(1));
    const hasManagePermission$ = this.permissionsService.hasPermissions({
      UserID: granteeId,
      Permissions: [Permission.CAN_CONFIGURE],
      ObjectID: appId,
      ObjectType: ObjectType.APP,
    });

    return forkJoin([userApps$, hasManagePermission$]).pipe(
      map(([apps, canManage]) => {
        // Find the app we're checking permissions for
        const app = apps.find(a => a.ID === appId);

        // Return false if app not found
        if (!app) {
          return false;
        }

        // User can manage if they own the app or have manage permission
        return app.Owner === granteeId || canManage;
      })
    );
  }


  /**
   * Returns an observable that emits the user's apps. More critically,
   * if user apps have already been fetched from server, returns an
   * observable that emits that cached array; otherwise triggers a fetch
   * from the API and returns observable that emits the response from
   * the server.
   */
  getUserApps$(): Observable<App[]> {
    if (this.fetchedUserAppsOnce) {
      return of(Object.values(this.sessUserApps));
    }

    if (this.fetchUserAppsResults$) {
      return this.fetchUserAppsResults$;
    }

    this.fetchUserAppsResults$ = new Subject<App[]>();

    this.fetchUserApps$().subscribe({
      next: (apps) => {
        this.fetchUserAppsResults$.next(apps);
        this.fetchUserAppsResults$.complete();
        delete this.fetchUserAppsResults$;
      },
    });

    return this.fetchUserAppsResults$;
  }


  /**
   * Fetch all apps to which the session user has access.
   *
   * (Calls to this method are debounced. Debounce is configured in the
   * constructor.)
   *
   * @see _fetchUserApps$
   */
  fetchUserApps$(): Observable<App[]> {
    return new Observable<App[]>(
      subscriber => {
        this.http.get<App[]>(`${API_BASE_URL}/apps/user/${this.session.userId}`).subscribe(
          (apps: App[]): void => {
            apps = apps.map(a => App.create(a));

            this.enableControlsIfNeeded(apps);

            this.store.dispatch(new AppsFetchedAll(apps));

            subscriber.next(apps);
            subscriber.complete();

            this.fetchedUserAppsOnce = true;
          },
          (err) => {
            this.fetchUserAppsResults$.error(err);
            delete this.fetchUserAppsResults$;
          },
        );
      },
    );
  }


  fetchChildApps$(appId: AppId): Observable<App[]> {
    return new Observable<App[]>(
      (subscriber) => {
        this.http.get<App[]>(`${API_BASE_URL}/apps/${appId}/children`).subscribe(
          (childApps: App[]): void => {
            this.store.dispatch(new AppsFetchedMultiple(childApps));
            subscriber.next(childApps);
            subscriber.complete();
          },
          (error) => {
            subscriber.error(error);
          },
        );
      },
    );
  }


  /**
   * Given a set of apps belonging to the user, if any of the apps is one which
   * could necessitate giving access to the Controls view for the logged-in user,
   * the Controls view will be enabled.
   *
   * We do this to avoid enabling a Controls view for less sophisticated users.
   *
   * @param apps
   * @private
   */
  private enableControlsIfNeeded(apps: App[]): boolean {
    for (let app of apps) {
      if ([APP_TYPE_PICTURE, APP_TYPE_PROP_MANAGER].indexOf(app.Type) > -1) {
        this.asideService.enableControls();
        return true;
      }
    }

    return false;
  }


  createApp$(newApp: NewApp): Observable<App> {
    if (!newApp.Type) {
      throw new Error('missing App type');
    }

    return this.http.post<App>(`${API_BASE_URL}/apps`, newApp);
  }


  createChildApp$(parentAppId: AppId, newChildApp: NewApp): Observable<App> {
    if (!newChildApp.Type) {
      throw new Error('missing App type');
    }

    return this.http.post<App>(`${API_BASE_URL}/apps/${parentAppId}/children`, newChildApp);
  }


  patchAppName$(appId: AppId, name: string): Observable<any> {
    return this.patchApp$(appId, {
      Name: name,
    });
  }


  patchAppSubtitle$(appId: AppId, subtitle: string): Observable<any> {
    return this.patchApp$(appId, {
      Config: {
        Subtitle: {
          Text: subtitle,
        },
      },
    });
  }


  patchAppImage$(appId: AppId, imageUuid: string): Observable<any> {
    return this.patchApp$(appId, {
      Image: imageUuid,
    });
  }


  patchAppPostalCode$(appId: AppId, postalCode: string): Observable<any> {
    return this.patchApp$(appId, {
      Config: {
        Location: {
          PostalCode: postalCode,
        },
      },
    });
  }


  patchChildAppSubtitles$(parentAppId: AppId, subtitle: string): Observable<any> {
    return this.patchChildApps$(parentAppId, {
      Config: {
        Subtitle: {
          Text: subtitle,
        },
      },
    });
  }


  patchChildAppImages$(parentAppId: AppId, imageUuid: string): Observable<any> {
    return this.patchChildApps$(parentAppId, {
      Image: imageUuid,
    });
  }


  patchChildAppPostalCodes$(parentAppId: AppId, postalCode: string): Observable<any> {
    return this.patchChildApps$(parentAppId, {
      Config: {
        Location: {
          PostalCode: postalCode,
        },
      },
    });
  }


  patchAppTemplate$(appId: AppId, template: Template): Observable<any> {
    return this.patchApp$(appId, {
      Config: {
        Template: template,
      },
    });
  }


  private patchApp$(appId: AppId, patch: AppPatch): Observable<any> {
    return this.http.patch(`${API_BASE_URL}/apps/${appId}`, patch).pipe(
      tap(() => {
            this.fetchApp(appId);
      })
    );
  }


  private patchChildApps$(parentAppId: AppId, patch: AppPatch): Observable<any> {
    return this.http.patch(`${API_BASE_URL}/apps/${parentAppId}/children`, patch).pipe(
      tap(() => {
            this.fetchChildApps$(parentAppId).subscribe();
      })
    );
  }


  saveAppTileGroups$(appId: AppId, tileGroups: TileGroup[]): Observable<null> {
    return new Observable<any>(subscriber => {
      this.http.put<null>(`${API_BASE_URL}/apps/${appId}/tile-groups`, tileGroups).subscribe(
        () => {
          this.store.dispatch(new AppsSavedTileGroups(appId, JSON.parse(JSON.stringify(tileGroups))));

          subscriber.next(null);
          subscriber.complete();
        },
        (err) => {
          subscriber.error(err);
        },
      );
    });
  }

  /**
   * Get apps linked to a specific tile
   * Uses the new /apps/tile/{tileID} endpoint
   */
  getAppsLinkedToTile$(tileId: TileId): Observable<App[]> {
    // PLAT-266: Return empty array for mock canvas tile
    if (tileId === 'mock-canvas-tile-id') {
      return of([]);
    }

    return this.http.get<App[]>(`${API_BASE_URL}/apps/tile/${tileId}`).pipe(
      map(apps => apps.map(app => App.create(app)))
    );
  }
}
